// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { Alert, Button, Card, Chip, Typography } from '@mui/material';
import {
  ScreenHeader,
  ScreenLayout,
} from '@shared/advisory/account-workflow/layout';
import { AppIcon } from '@shared/ui-kit/components';
import { classNames } from '@shared/ui-kit/utils';
import { Translation } from '@shared/ui-kit/providers';
import { defineComponent } from '@shared/ui-kit/utils/define-component';
import { usePendingChecklist } from './use-pending-checklist';

type StageStatusChipProps =
  | {
      variant: 'completed';
    }
  | {
      variant: 'pending';
    }
  | {
      variant: 'duration';
      durationKey:
        | 'combined-flow.welcome.stage-one.duration'
        | 'combined-flow.welcome.stage-two.duration';
    };

const StageStatusChip = (props: StageStatusChipProps) => {
  if (props.variant === 'completed') {
    return (
      <Chip
        size="small"
        color="success"
        className="!max-h-6 min-w-20 shrink-0 !font-semibold"
        label={
          <Translation
            i18nKey="combined-flow.welcome.stage-one.completed"
            ns="components"
          />
        }
      />
    );
  }

  if (props.variant === 'pending') {
    return (
      <Chip
        size="small"
        color="warning"
        className="!max-h-6 min-w-20 shrink-0 !font-semibold"
        label={
          <Translation
            i18nKey="combined-flow.welcome.stage-two.pending"
            ns="components"
          />
        }
      />
    );
  }

  return (
    <Chip
      size="small"
      variant="outlined"
      color="default"
      className="!max-h-6 shrink-0"
      icon={<AppIcon icon="Clock" size="sm" color="dark" />}
      label={<Translation i18nKey={props.durationKey} ns="components" />}
    />
  );
};

export const PendingChecklist = defineComponent({
  displayName: 'PendingChecklist',
  useSetup: usePendingChecklist,
  View: ({
    advisoryTypeName,
    hasExistingInversisAccount,
    hasCompletedProfiling,
    hasPartialProfilingData,
    canResumeToProfilingResults,
    isAwaitingContractSignature,
    onStart,
  }) => {
    const stageOneCompleted = hasExistingInversisAccount;
    const stageTwoCompleted =
      hasCompletedProfiling ||
      canResumeToProfilingResults ||
      isAwaitingContractSignature;
    const stageTwoInProgress = hasPartialProfilingData && !stageTwoCompleted;

    return (
      <ScreenLayout className="items-center">
        <div className="mx-auto flex w-full max-w-2xl flex-col gap-8 text-center">
          <ScreenHeader
            title={
              <Translation i18nKey="combined-flow.welcome.title" ns="components" />
            }
            subtitle={
              <Translation
                i18nKey="combined-flow.welcome.subtitle"
                ns="components"
                values={{ advisoryType: advisoryTypeName }}
              />
            }
            className="!items-center"
            titleProps={{ className: '!text-center' }}
            subtitleProps={{ className: '!text-center' }}
          />

          <div className="flex items-center justify-center gap-2">
            <AppIcon icon="Clock" size="md" color="dark" className="shrink-0" />
            <Typography variant="body2" className="!text-subtitle">
              <Translation
                i18nKey="combined-flow.welcome.total-time"
                ns="components"
                values={{ minutes: 15 }}
              />
            </Typography>
          </div>

          <Card
            variant="outlined"
            className="!rounded-2xl !border-border-light !p-0 text-left"
          >
            <div className="flex flex-col divide-y divide-border-light">
              <div
                className={classNames('flex gap-4 p-5', {
                  'items-center': stageOneCompleted,
                  'items-start': !stageOneCompleted,
                })}
              >
                <span
                  className={classNames(
                    'flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-body font-semibold text-white',
                    stageOneCompleted ? 'bg-positive' : 'bg-secondary',
                    !stageOneCompleted && 'mt-0.5',
                  )}
                >
                  1
                </span>

                <div className="flex min-w-0 flex-1 flex-col gap-2">
                  <div className="flex min-h-8 items-center justify-between gap-4">
                    <Typography
                      variant="h5"
                      className={classNames('!font-semibold', {
                        '!text-positive': stageOneCompleted,
                        '!text-title-navy': !stageOneCompleted,
                      })}
                    >
                      <Translation
                        i18nKey="combined-flow.welcome.stage-one.title"
                        ns="components"
                      />
                    </Typography>

                    {stageOneCompleted ? (
                      <StageStatusChip variant="completed" />
                    ) : (
                      <StageStatusChip
                        variant="duration"
                        durationKey="combined-flow.welcome.stage-one.duration"
                      />
                    )}
                  </div>

                  {!stageOneCompleted && (
                    <Typography variant="body2" className="!text-subtitle">
                      <Translation
                        i18nKey="combined-flow.welcome.stage-one.description"
                        ns="components"
                      />
                    </Typography>
                  )}
                </div>
              </div>

              <div
                className={classNames('flex gap-4 p-5', {
                  'items-center': stageTwoCompleted,
                  'items-start': !stageTwoCompleted,
                })}
              >
                <span
                  className={classNames(
                    'flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-body font-semibold text-white',
                    stageTwoCompleted ? 'bg-positive' : 'bg-secondary',
                    !stageTwoCompleted && 'mt-0.5',
                  )}
                >
                  2
                </span>

                <div className="flex min-w-0 flex-1 flex-col gap-2">
                  <div className="flex min-h-8 items-center justify-between gap-4">
                    <Typography
                      variant="h5"
                      className={classNames('!font-semibold', {
                        '!text-positive': stageTwoCompleted,
                        '!text-title-navy': !stageTwoCompleted,
                      })}
                    >
                      <Translation
                        i18nKey="combined-flow.welcome.stage-two.title"
                        ns="components"
                      />
                    </Typography>

                    {stageTwoCompleted ? (
                      <StageStatusChip variant="completed" />
                    ) : stageTwoInProgress ? (
                      <StageStatusChip variant="pending" />
                    ) : (
                      <StageStatusChip
                        variant="duration"
                        durationKey="combined-flow.welcome.stage-two.duration"
                      />
                    )}
                  </div>

                  {!stageTwoCompleted && (
                    <Typography variant="body2" className="!text-subtitle">
                      <Translation
                        i18nKey="combined-flow.welcome.stage-two.description"
                        ns="components"
                        values={{ advisoryType: advisoryTypeName }}
                      />
                    </Typography>
                  )}
                </div>
              </div>
            </div>
          </Card>

          <Alert severity="info" className="!rounded-2xl text-left" icon={false}>
            <div className="flex items-center gap-3">
              <AppIcon
                icon="CheckCircle"
                size="lg"
                color="secondary"
                className="shrink-0"
              />
              <Typography variant="body2" className="!text-subtitle">
                <Translation
                  i18nKey={
                    isAwaitingContractSignature
                      ? 'combined-flow.welcome.profiling-signature-pending-alert'
                      : canResumeToProfilingResults
                      ? 'combined-flow.welcome.profiling-resume-alert'
                      : hasPartialProfilingData
                      ? 'combined-flow.welcome.profiling-partial-alert'
                      : hasExistingInversisAccount
                      ? 'combined-flow.welcome.inversis-alert-existing'
                      : 'combined-flow.welcome.inversis-alert'
                  }
                  ns="components"
                />
              </Typography>
            </div>
          </Alert>

          <Typography
            variant="caption"
            className="text-center !text-subtitle"
            component="p"
          >
            <Translation
              i18nKey="combined-flow.welcome.legal-disclaimer"
              ns="components"
            />
          </Typography>

          <div className="flex justify-center pb-4">
            <Button
              color="secondary"
              data-testid="next-button"
              size="large"
              endIcon={<AppIcon icon="ArrowRight" />}
              type="button"
              onClick={onStart}
            >
              <Translation
                i18nKey="combined-flow.welcome.start-button"
                ns="components"
              />
            </Button>
          </div>
        </div>
      </ScreenLayout>
    );
  },
});
